import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import Index from "./pages/Index.tsx";
import { InstallPrompt } from "@/components/InstallPrompt";
import { KeyboardShortcuts } from "@/components/KeyboardShortcuts";
import logoCompass from "@/assets/logo-compass.png";

// Route-level code splitting — keep the Index bundle small and load
// secondary routes only when navigated to.
const ResetPassword = lazy(() => import("./pages/ResetPassword.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const CardPage = lazy(() => import("./pages/CardPage.tsx"));
const SetPage = lazy(() => import("./pages/SetPage.tsx"));
const SetsIndexPage = lazy(() => import("./pages/SetsIndexPage.tsx"));
const RotationPage = lazy(() => import("./pages/RotationPage.tsx"));
const BannedPage = lazy(() => import("./pages/BannedPage.tsx"));
const PricesPage = lazy(() => import("./pages/PricesPage.tsx"));
const BroomDeckGuidePage = lazy(() => import("./pages/BroomDeckGuidePage.tsx"));
const MetaTierListPage = lazy(() => import("./pages/MetaTierListPage.tsx"));
const PublicProfilePage = lazy(() => import("./pages/PublicProfilePage.tsx"));
const PublicDeckPage = lazy(() => import("./pages/PublicDeckPage.tsx"));
const DecksOfTheWeekPage = lazy(() => import("./pages/DecksOfTheWeekPage.tsx"));
const DeckBuilderLandingPage = lazy(() => import("./pages/DeckBuilderLandingPage.tsx"));
const InkPairPage = lazy(() => import("./pages/InkPairPage.tsx"));
const CollectionValuePage = lazy(() => import("./pages/CollectionValuePage.tsx"));
const CollectionImportPage = lazy(() => import("./pages/CollectionImportPage.tsx"));
const NewsPage = lazy(() => import("./pages/NewsPage.tsx"));
const DigestArticlePage = lazy(() => import("./pages/DigestArticlePage.tsx"));
const AdminNewsPanel = lazy(() => import("./pages/AdminNewsPanel.tsx"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe.tsx"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent.tsx"));


const queryClient = new QueryClient();

const RouteFallback = () => (
  <div className="flex min-h-screen items-center justify-center">
    <img
      src={logoCompass}
      alt="Loading LoreScout"
      width={48}
      height={48}
      className="h-12 w-12 animate-spin"
      style={{ animationDuration: "3s" }}
    />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <KeyboardShortcuts />
          <InstallPrompt />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/cards/:slug" element={<CardPage />} />
              <Route path="/sets" element={<SetsIndexPage />} />
              <Route path="/sets/:slug" element={<SetPage />} />
              <Route path="/rotation" element={<RotationPage />} />
              <Route path="/banned" element={<BannedPage />} />
              <Route path="/prices" element={<PricesPage />} />
              <Route path="/guides/broom-deck" element={<BroomDeckGuidePage />} />
              <Route path="/meta" element={<MetaTierListPage />} />
              <Route path="/tier-list" element={<MetaTierListPage />} />
              <Route path="/u/:userId" element={<PublicProfilePage />} />
              <Route path="/decks/top" element={<DecksOfTheWeekPage />} />
              <Route path="/deck-builder" element={<DeckBuilderLandingPage />} />
              <Route path="/decks/ink/:pair" element={<InkPairPage />} />
              <Route path="/collection/value" element={<CollectionValuePage />} />
              <Route path="/collection/import" element={<CollectionImportPage />} />
              <Route path="/decks/:deckId" element={<PublicDeckPage />} />

              <Route path="/news" element={<NewsPage />} />
              <Route path="/news/:slug" element={<DigestArticlePage />} />
              <Route path="/admin/news" element={<AdminNewsPanel />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
